/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

import { getActualKey } from '../input/keys.js';

/**
 * Svelte Action to provide the ordering capabilities
 * @example
 *   <table
 *     use:orderableChildren={{ enabled: true, delay: 100, selector: `tr.orderable` }}
 *     on:order={handleReorder}>
 *      <tr><td>one</td></tr>
 *      <tr><td>two</td></tr>
 *      <tr><td>three</td></tr>
 *   </table>
 *  />
 *
 *
 *
 * @typedef {Object} KeyboardOrderEvent keyboard order custom event
 * @property {Object} detail
 * @property {number} detail.index - initial position keyboard ordering
 *
 * @typedef {Object} PlacementEvent keyboard placement custom event
 * @property {Object} detail
 * @property {number} detail.index - initial position keyboard ordering
 *
 * @typedef {Object} OrderEvent order custom event
 * @property {Object} detail
 * @property {number} detail.initialPosition - initial position of the selected element
 * @property {number} detail.newPosition - new position of the selected element
 *
 * @typedef {Object} OrderCancelled ordercancelled custom event
 *
 * @param {HTMLElement} node - the target node the action applies to
 * @param {Object} options - options
 * @param {Number} options.enabled - mousedown delay to start reordering
 * @param {Number} options.delay - mousedown delay to start reordering
 * @param {String} options.selector - orderable child selector
 * @param {String} options.selectedClass - class to be applied to currently selected element
 * @param {String} options.orderingClass - class to be applied to parent while ordering
 * @param {String} options.orderingClassKeyboard - class to be applied to parent while ordering bu keyboard
 * @param {String} options.placementClass - class to be applied to child after which to place the element
 * @returns {Object} - target's lifecycle hooks
 * @fires {OrderEvent} order - custom event on ordering performed
 * @fires {OrderCancelled} ordercancelled - custom event on ordering cancel
 * @fires {PlacementEvent} placement - custom event on keyboard intended placement change
 */
export default function orederableChildren(
    node,
    {
        delay = 200,
        selector,
        enabled = true,
        orderingClass = 'orderable-children-ordering',
        orderingClassKeyboard = 'orderable-children-ordering-keyboard',
        selectedClass = 'orderable-children-selected',
        placementClass = 'orderable-children-placement'
    }
) {
    let orderingStartTimeout;
    let selectedChild;
    let placementIndex;
    let maxPlacementIndex;
    let keyboardSelected;

    const indexAttribute = 'data-orderable-children-index';

    /**
     * Handler to be invoked on container mousedown
     * @param {MouseEvent} event mouse event
     */
    function handleMousedown(event) {
        addIndexAttributes();
        selectedChild = event.target.closest(selector);
        if (selectedChild) {
            orderingStartTimeout = setTimeout(startOrdering, delay);

            window.addEventListener('mouseup', handleWindowMouseup);
        }
    }

    /**
     * Handler to be invoked on window mouseup
     * @param {MouseEvent} event mouse event
     */
    function handleWindowMouseup(event) {
        if (orderingStartTimeout) {
            cancelOrderingStartTimeout();
        } else {
            finishOrdering(event);
        }
        window.removeEventListener('mouseup', handleWindowMouseup);
        window.removeEventListener('mousemove', handleWindowMousemove);
    }

    /**
     * Handler to be invoked on window keydown
     * @param {KeyboardEvent} event keyboard event
     */
    function handleWindowKeydown(event) {
        const key = getActualKey(event);

        switch (key) {
            case 'esc':
                cancelKeyboardOrdering();
                break;
            case 'up':
                placementIndex = placementIndex === 0 ? placementIndex : placementIndex - 1;
                setPlacement();
                break;
            case 'down':
                placementIndex = placementIndex === maxPlacementIndex ? maxPlacementIndex : placementIndex + 1;
                setPlacement();
                break;
            case 'space':
            case 'enter':
                finishKeyboardOrdering();
                break;
        }
        event.stopPropagation();
    }

    /**
     * Cancels orderingStartTimeout
     */
    function cancelOrderingStartTimeout() {
        clearTimeout(orderingStartTimeout);
        orderingStartTimeout = void 0;
        selectedChild = void 0;
        removeIndexAttributes();
    }

    /**
     * Adds classes to container and children, adds `mousemove` listener to window
     */
    function startOrdering() {
        orderingStartTimeout = void 0;
        node.classList.add(orderingClass);
        selectedChild.classList.add(selectedClass);

        window.addEventListener('mousemove', handleWindowMousemove);
    }

    /**
     * Handler for window mousemove event
     * @param {MouseEvent} event mouse event
     */
    function handleWindowMousemove(event) {
        if (event.buttons !== 1) {
            window.removeEventListener('mousemove', handleWindowMousemove);
            finishOrdering(event);
        }
    }

    /**
     * Finishes the ordering by removing ordering classes and attibuttes
     * calls event dispatch routine
     * @param {MouseEvent} event mouse event
     */
    function finishOrdering(event) {
        removeOrderingClasses();
        //check if ordering finished over some of orderable child
        let hoveredChild = getElementFromPoint(event.clientX, event.clientY);

        if (hoveredChild) {
            //some elements like tr doesn't appear in the event details
            dispatchOrderEvent(
                parseInt(selectedChild.getAttribute(indexAttribute)),
                parseInt(hoveredChild.getAttribute(indexAttribute))
            );
        } else {
            dispatchCancelEvent();
        }

        removeIndexAttributes();
    }

    /**
     * Handler for keyboard ordering start event
     * @param {KeyboardOrderEvent} event keyboardorder custom event
     */
    function handleKeyboardOrder(event) {
        //disable mouse ordering
        node.removeEventListener('mousedown', handleMousedown);
        const index = event.detail.index;
        keyboardSelected = index;
        placementIndex = index;
        node.classList.add(orderingClass);
        node.classList.add(orderingClassKeyboard);
        const allChildren = node.querySelectorAll(selector);
        selectedChild = allChildren[index];
        maxPlacementIndex = allChildren.length - 1;
        selectedChild.classList.add(selectedClass);
        setPlacement();
        window.addEventListener('keydown', handleWindowKeydown, true);
    }

    /**
     * Adds placementClass to the desired placement element,
     * scrolls to make element fully visible on the screen
     */
    function setPlacement() {
        //gap to add to scrolling (px)
        const scrollExtraPx = 10;
        const orderableChildren = node.querySelectorAll(selector);
        let placementElement = orderableChildren[placementIndex];

        orderableChildren.forEach((placementChild, index) => {
            if (placementIndex === index) {
                placementChild.classList.add(placementClass);
            } else {
                placementChild.classList.remove(placementClass);
            }
        });

        //scroll to make placement element fully visible
        const { top, bottom } = placementElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (top < 0) {
            window.scrollBy({
                top: top - scrollExtraPx,
                behavior: 'smooth'
            });
        }
        if (bottom > viewportHeight) {
            window.scrollBy({
                top: bottom - viewportHeight + scrollExtraPx,
                behavior: 'smooth'
            });
        }

        dispatchPlacementEvent(placementIndex);
    }

    /**
     * Performes routines on finish of keyboard ordering
     */
    function finishKeyboardOrdering() {
        removeOrderingClasses();

        dispatchOrderEvent(keyboardSelected, placementIndex);

        //enable again mouse ordering
        node.addEventListener('mousedown', handleMousedown);

        removeIndexAttributes();
        window.removeEventListener('keydown', handleWindowKeydown, true);

        placementIndex = void 0;
    }

    /**
     * Dispatches order event on the container
     * @param {number} initialPosition index of selected element
     * @param {number} newPosition index of desired place for element
     * @fires {OrderEvent}
     */
    function dispatchOrderEvent(initialPosition, newPosition) {
        if (initialPosition === newPosition) {
            dispatchCancelEvent();
            return;
        }

        const orderEvent = new CustomEvent('order', {
            detail: {
                initialPosition,
                newPosition
            }
        });
        node.dispatchEvent(orderEvent);
    }

    /**
     * @fires {OrderCancelled}
     */
    function dispatchCancelEvent() {
        const cancelEvent = new CustomEvent('ordercancelled');
        node.dispatchEvent(cancelEvent);
    }

    /**
     * Dispatches placement event
     * @param {number} index index of selected element
     * @fires {PlacementEvent} placement
     */
    function dispatchPlacementEvent(index) {
        const palcementEvent = new CustomEvent('placement', {
            detail: {
                index
            }
        });
        node.dispatchEvent(palcementEvent);
    }

    /**
     * Determines what element is intended to be the place for the selected
     * @param {number} clientX x coordinate
     * @param {number} clientY y coordinate
     * @returns {HTMLElement}
     */
    function getElementFromPoint(clientX, clientY) {
        const topmostElement = document.elementFromPoint(clientX, clientY);
        const findInParent = el => {
            if (el && el.matches) {
                if (el.matches(selector)) {
                    return el;
                } else {
                    return findInParent(el.parentElement);
                }
            } else {
                return null;
            }
        };
        return findInParent(topmostElement);
    }

    /**
     * Cancels the keyboard ordering
     */
    function cancelKeyboardOrdering() {
        placementIndex = void 0;
        removeIndexAttributes();
        removeOrderingClasses();
        clearEventListeners();
        addEventListeners();
        dispatchCancelEvent();
    }
    /**
     * Adds index data attributes to orderable children
     */
    function addIndexAttributes() {
        node.querySelectorAll(selector).forEach((childNode, i) => {
            childNode.setAttribute(indexAttribute, i);
        });
    }

    /**
     * Removes index data attributes to orderable children
     */
    function removeIndexAttributes() {
        node.querySelectorAll(selector).forEach(childNode => {
            childNode.removeAttribute(indexAttribute);
        });
    }

    /**
     * Removes orderable classes from container and children
     */
    function removeOrderingClasses() {
        node.classList.remove(orderingClass);
        node.classList.remove(orderingClassKeyboard);
        node.querySelectorAll(selector).forEach(placementChild => {
            placementChild.classList.remove(placementClass);
            placementChild.classList.remove(selectedClass);
        });
    }

    /**
     * Adds necessary event listeners to container
     */
    function addEventListeners() {
        node.addEventListener('mousedown', handleMousedown);
        node.addEventListener('keyboardorder', handleKeyboardOrder);
    }

    /**
     * Removes initial and any other additional event listeners
     * from container and window
     */
    function clearEventListeners() {
        node.removeEventListener('mousedown', handleMousedown);
        node.removeEventListener('keyboardorder', handleKeyboardOrder);
        window.removeEventListener('mousemove', handleWindowMousemove);
        window.removeEventListener('mouseup', handleWindowMouseup);
        window.removeEventListener('keydown', handleWindowKeydown, true);
    }

    //entry point of the action
    if (enabled) {
        addEventListeners();
    }

    return {
        update({ enabled: updatedEnable }) {
            if (updatedEnable) {
                clearEventListeners();
                addEventListeners();
            } else {
                clearEventListeners();
            }
        },
        destroy: () => {
            clearEventListeners();
        }
    };
}
